/**
 * 全局 WebSocket 服务
 * 单例模式，所有组件共享同一个连接
 */
import io from 'socket.io-client'
import Vue from 'vue'
import axios from 'axios'
import { t } from '@/i18n/helpers'

class WebSocketService {
  constructor() {
    this.socket = null
    this.connected = false
    this.registered = false
    this.userId = null
    // 使用 Vue 实例作为事件总线
    this.eventBus = new Vue()
    this.heartbeatTimer = null
    // 记录已注册的监听器，防止重复注册
    this.registeredListeners = new Map()
    // 离线消息队列：断开连接时暂存消息，重连注册成功后自动发送
    this.messageQueue = []
    this.maxQueueSize = 100
  }

  /**
   * 连接 WebSocket
   * @param {string} chatHost - WebSocket 服务器地址
   * @param {string} token - 用户 token
   */
  connect(chatHost, token) {
    if (this.socket && this.connected) {
      console.log('[WebSocket] Already connected')
      return
    }

    if (!token) {
      console.log('[WebSocket] No token, skip connect')
      return
    }

    // 清理旧 socket，防止旧连接的重连/断开回调污染新连接状态
    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }
    this.connected = false
    this.registered = false
    this.stopHeartbeat()

    console.log('[WebSocket] Connecting to:', chatHost)
    
    this.socket = io(chatHost, { 
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 999999,
      reconnectionDelay: 3000
    })

    this.socket.on('connect', () => {
      console.log('[WebSocket] Connected')
      this.connected = true
      this.eventBus.$emit('connected')
      this.socket.emit('register', { token })
    })

    this.socket.on('register', (msg) => {
      if (msg && msg.code == 0) {
        console.log('[WebSocket] Registered, userId:', msg.userId)
        this.registered = true
        this.userId = msg.userId
        this.eventBus.$emit('registered', msg)
        this.startHeartbeat()
        // 注册成功后，发送队列中的待发消息
        this._flushMessageQueue()
      } else {
        console.log('[WebSocket] Register failed:', msg)
        this.registered = false
      }
    })

    this.socket.on('disconnect', () => {
      console.log('[WebSocket] Disconnected')
      this.connected = false
      this.registered = false
      this.eventBus.$emit('disconnected')
      this.stopHeartbeat()
    })

    this.socket.on('connect_error', (error) => {
      console.log('[WebSocket] Connect error:', error)
      this.connected = false
      this.registered = false
      this.eventBus.$emit('error', error)
    })

    // 监听消息
    this.socket.on('msg', (msg) => {
      console.log('[WebSocket] Message received:', msg)
      // 收到消息后，立即发送 client_ack 告诉发送方消息已到达本客户端
      // 注意：client_ack 表示消息已到达设备，与 endpoint_ack（用户已读）不同
      if (msg && msg.msgId && msg.fromUserId && msg.fromUserId !== '20210802') {
        this.sendClientAck(msg.msgId, msg.fromUserId)
      }
      this.eventBus.$emit('msg', msg)
    })

    // 服务器确认收到消息
    this.socket.on('server_ack', (msg) => {
      console.log('[WebSocket] server_ack:', msg)
      this.eventBus.$emit('server_ack', msg)
    })

    // 对方已读消息
    this.socket.on('endpoint_ack', (msg) => {
      console.log('[WebSocket] endpoint_ack:', msg)
      this.eventBus.$emit('endpoint_ack', msg)
    })

    // 消息被屏蔽（发送者被对方屏蔽）
    this.socket.on('msg_blocked', (msg) => {
      console.log('[WebSocket] msg_blocked:', msg)
      this.eventBus.$emit('msg_blocked', msg)
    })

    // 消息撤回事件（双方都会收到，用于本地把消息替换为撤回占位）
    this.socket.on('msg_revoke', (msg) => {
      console.log('[WebSocket] msg_revoke:', msg)
      this.eventBus.$emit('msg_revoke', msg)
    })

    // 群内新 chat_session 推送
    this.socket.on('channel_chat_session', (msg) => {
      console.log('[WebSocket] channel_chat_session:', msg)
      this.eventBus.$emit('channel_chat_session', msg)
    })

    // 评论推送
    this.socket.on('chat_session_comment', (msg) => {
      console.log('[WebSocket] chat_session_comment:', msg)
      this.eventBus.$emit('chat_session_comment', msg)
      this.eventBus.$emit('interaction_notification', msg)
    })

    // 点赞推送
    this.socket.on('chat_session_like', (msg) => {
      console.log('[WebSocket] chat_session_like:', msg)
      this.eventBus.$emit('chat_session_like', msg)
      this.eventBus.$emit('interaction_notification', msg)
    })

    // 匹配结果推送（服务端保存匹配后实时下发，刷新 Matches 未读数字与角标）
    this.socket.on('match_found', (msg) => {
      console.log('[WebSocket] match_found:', msg)
      this.eventBus.$emit('match_found', msg)
    })

    // 重连成功后重新注册
    this.socket.on('reconnect', () => {
      console.log('[WebSocket] Reconnected')
      this.connected = true
      this.registered = false
      this.eventBus.$emit('reconnect')
      if (token) {
        this.socket.emit('register', { token })
      }
    })
  }

  /**
   * 断开连接
   */
  disconnect() {
    if (this.socket) {
      console.log('[WebSocket] Disconnecting...')
      this.stopHeartbeat()
      this.socket.disconnect()
      this.socket = null
      this.connected = false
      this.registered = false
      this.userId = null
    }
    // 清理所有已注册的监听器
    this.clearAllListeners()
  }

  /**
   * 清理所有监听器
   */
  clearAllListeners() {
    this.registeredListeners.clear()
  }

  /**
   * 发送消息（支持离线队列）
   */
  sendMessage(data) {
    if (this.socket && this.connected && this.registered) {
      this.socket.emit('msg', data)
      return true
    }
    console.log('[WebSocket] Cannot send: not ready (connected=' + this.connected + ', registered=' + this.registered + '), queuing message')
    // 未连接时，将消息加入离线队列
    this._enqueueMessage(data)
    // 触发发送失败事件，通知组件更新消息状态
    this.eventBus.$emit('send_failed', {
      msgId: data.msgId,
      error: t('network_disconnected')
    })
    return false
  }

  /**
   * 将消息加入离线队列
   */
  _enqueueMessage(data) {
    if (this.messageQueue.length >= this.maxQueueSize) {
      this.messageQueue.shift() // 队列满了，移除最旧的消息
    }
    this.messageQueue.push(data)
    console.log('[WebSocket] Message queued, queue size:', this.messageQueue.length)
  }

  /**
   * 刷新离线消息队列（注册成功后调用）
   */
  _flushMessageQueue() {
    if (this.messageQueue.length === 0) return
    console.log('[WebSocket] Flushing message queue, size:', this.messageQueue.length)
    const queue = [...this.messageQueue]
    this.messageQueue = []
    for (const data of queue) {
      if (this.socket && this.connected && this.registered) {
        this.socket.emit('msg', data)
        console.log('[WebSocket] Flushed queued message:', data.msgId)
      } else {
        // 如果又断开了，重新入队
        this._enqueueMessage(data)
        break
      }
    }
  }

  /**
   * 发送已读确认
   * WS 可用时走 socket endpoint_ack；断开/弱网时降级为 HTTP 兜底，避免已读状态丢失
   */
  sendEndpointAck(msgId, toUserId) {
    if (this.socket && this.connected && this.registered) {
      this.socket.emit('endpoint_ack', {
        msgId,
        toUserId,
        code: 0
      })
      console.log('[WebSocket] Sent endpoint_ack:', msgId, toUserId)
      return
    }
    // WS 不可用，走 HTTP 兜底上报已读
    this.sendEndpointAckHttp(msgId, toUserId)
  }

  /**
   * 已读上报 HTTP 兜底：调用 /chat/update_msg_read（fire-and-forget，带去重）
   * 注意：HTTP 兜底只落库，对方端要等下次拉取历史消息才能看到已读状态
   */
  sendEndpointAckHttp(msgId, fromUserId) {
    if (!msgId || !fromUserId) return
    if (!this._readAckFallbackSent) {
      this._readAckFallbackSent = new Set()
    }
    if (this._readAckFallbackSent.has(msgId)) return
    this._readAckFallbackSent.add(msgId)
    // toUserId（已读人）由后端从 JWT 解析，只需传消息发送者 fromUserId
    axios.post('/api/chat/update_msg_read', {
      msgId: msgId,
      fromUserId: fromUserId
    }).catch(err => {
      // 失败后移除标记，允许下次重试
      this._readAckFallbackSent.delete(msgId)
      console.error('[WebSocket] endpoint_ack HTTP fallback failed:', msgId, err)
    })
  }

  /**
   * 发送送达确认（告诉对方消息已到达本客户端，但用户可能未读）
   */
  sendClientAck(msgId, toUserId) {
    if (this.socket && this.connected && this.registered) {
      this.socket.emit('client_ack', {
        msgId,
        toUserId,
        code: 0
      })
      console.log('[WebSocket] Sent client_ack:', msgId, toUserId)
    }
  }

  /**
   * 撤回消息
   * WS 可用时走 socket msg_revoke（服务端撤回后向双方广播实时事件）；
   * 断开/弱网时降级为 HTTP 兜底（仅落库，对方下次拉取历史消息可见），返回 Promise 供调用方感知结果
   */
  sendMsgRevoke(msgId, toUserId) {
    if (this.socket && this.connected && this.registered) {
      this.socket.emit('msg_revoke', {
        msgId,
        toUserId
      })
      console.log('[WebSocket] Sent msg_revoke:', msgId, toUserId)
      return Promise.resolve({ code: 0 })
    }
    // WS 不可用，走 HTTP 兜底撤回
    return axios.post('/api/chat/revoke_msg', {
      msgId: msgId
    }).then(resp => resp.data).catch(err => {
      console.error('[WebSocket] msg_revoke HTTP fallback failed:', msgId, err)
      return { code: 1, data: 'network error' }
    })
  }

  /**
   * 发送心跳
   */
  sendHeartbeat() {
    if (this.socket && this.connected && this.registered) {
      this.socket.emit('heartbeat', {})
    }
  }

  /**
   * 开始心跳定时器
   */
  startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      this.sendHeartbeat()
    }, 30000)
  }

  /**
   * 停止心跳定时器
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  /**
   * 监听事件
   * 注意：使用命名监听器模式，同一名称的监听器只会注册一次
   * @param {string} event - 事件名
   * @param {function} callback - 回调函数
   * @param {string} name - 监听器名称（可选，用于防重复）
   */
  on(event, callback, name) {
    // 如果提供了名称，先移除同名的旧监听器
    if (name) {
      const key = `${event}:${name}`
      if (this.registeredListeners.has(key)) {
        const oldCallback = this.registeredListeners.get(key)
        this.eventBus.$off(event, oldCallback)
      }
      this.registeredListeners.set(key, callback)
    }
    this.eventBus.$on(event, callback)
  }

  /**
   * 取消监听
   * @param {string} event - 事件名
   * @param {function} callback - 回调函数或监听器名称
   */
  off(event, callback) {
    if (typeof callback === 'string') {
      // callback 是名称，查找对应的函数
      const key = `${event}:${callback}`
      const actualCallback = this.registeredListeners.get(key)
      if (actualCallback) {
        this.eventBus.$off(event, actualCallback)
        this.registeredListeners.delete(key)
      }
    } else {
      // callback 是函数
      this.eventBus.$off(event, callback)
      // 也尝试从 registeredListeners 中移除
      for (const [key, cb] of this.registeredListeners) {
        if (cb === callback && key.startsWith(`${event}:`)) {
          this.registeredListeners.delete(key)
          break
        }
      }
    }
  }

  /**
   * 监听事件（一次性）
   */
  once(event, callback) {
    this.eventBus.$once(event, callback)
  }

  /**
   * 是否已连接
   */
  isConnected() {
    return this.connected && this.registered
  }
}

// 导出单例
export const wsService = new WebSocketService()
export default wsService



// WEBPACK FOOTER //
// ./src/services/websocket.js